import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";

import { Skeleton } from "@/components/ui/skeleton";
import { 
  FileEdit, 
  ExternalLink, 
  ArrowRight, 
  Plus,
  Calendar,
  Building,
  MapPin,
  Eye,

} from "lucide-react";
import { cn } from "@/lib/utils";
import { useJobDescriptions } from './hooks/useJobDescriptions';


interface RecentJobDescriptionsProps {
  onNavigateToGenerator?: () => void;
  limit?: number;
}

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  return date.toLocaleDateString();
}

export function RecentJobDescriptions({ 
  onNavigateToGenerator, 
  limit = 5 
}: RecentJobDescriptionsProps) {
  const { jobDescriptions, isLoading } = useJobDescriptions();
  const navigate = useNavigate();

  // Filter and sort recent job descriptions
  const recentJobDescriptions = useMemo(() => {
    return jobDescriptions
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, limit);
  }, [jobDescriptions, limit]);

  // Helper function to handle navigation
  const handleNavigateToGenerator = () => {
    if (onNavigateToGenerator) {
      onNavigateToGenerator();
    } else {
      navigate('/?tab=job-generator');
    }
  };

  // Calculate activity stats
  const activityStats = useMemo(() => {
    const totalJobs = jobDescriptions.length;
    const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    const recentJobs = jobDescriptions.filter(jd => 
      new Date(jd.created_at) > oneWeekAgo
    ).length;

    return {
      totalJobs,
      recentJobs
    };
  }, [jobDescriptions]);

  // Loading State
  if (isLoading) {
    return (
      <div className="space-y-4 pt-2 min-h-[200px]">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="flex items-center space-x-4">
            <Skeleton className="h-9 w-9 rounded-lg" />
            <div className="space-y-1.5 flex-1">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Empty State
  if (recentJobDescriptions.length === 0) {
    return (
      <div className="text-center text-slate-500 py-8 flex flex-col items-center min-h-[200px] justify-center space-y-4">
        <FileEdit className="w-12 h-12 text-slate-400" />
        <div className="space-y-2">
          <p className="text-sm font-medium text-slate-600">
            No job descriptions yet
          </p>
          <p className="text-xs text-slate-500">
            Generate professional job descriptions with Smart technology to start building your hiring pipeline.
          </p>
        </div>
        <Button 
          size="sm" 
          variant="outline" 
          onClick={handleNavigateToGenerator}
          className="mt-4 border-slate-200 hover:bg-slate-50"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Job Description
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Activity Statistics */}
      {activityStats.totalJobs > 0 && (
        <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-slate-50 rounded-xl border border-slate-200">
          <div className="text-center space-y-1">
            <div className="text-2xl font-bold text-slate-900">{activityStats.recentJobs}</div>
            <div className="text-xs text-slate-600 font-medium">This Week</div>
          </div>
          <div className="text-center space-y-1">
            <div className="text-2xl font-bold text-slate-900">{activityStats.totalJobs}</div>
            <div className="text-xs text-slate-600 font-medium">Total</div>
          </div>
        </div>
      )}

      {/* Job Description Items */}
      <div className="space-y-2 max-h-72 overflow-y-auto">
        {recentJobDescriptions.map((jd) => (
          <div 
            key={jd.id} 
            className="flex items-center space-x-4 p-4 hover:bg-slate-50 rounded-xl transition-all duration-200 cursor-pointer group border border-transparent hover:border-slate-200"
            onClick={handleNavigateToGenerator}
          >
            <div className={cn(
              "w-12 h-12 flex items-center justify-center rounded-lg text-sm font-bold transition-all duration-200",
              jd.is_published 
                ? "bg-green-100 text-green-700 border border-green-200" 
                : "bg-slate-100 text-slate-700 border border-slate-200"
            )}>
              {jd.is_published ? (
                <Eye className="h-5 w-5" />
              ) : (
                <FileEdit className="h-5 w-5" />
              )}
            </div>
            <div className="min-w-0 flex-1 space-y-1">
              <p className="text-sm font-semibold text-slate-900 truncate group-hover:text-slate-700 transition-colors">
                {jd.title}
              </p>
              <div className="flex items-center gap-2 text-xs text-slate-600">
                {jd.company && (
                  <span className="flex items-center gap-1">
                    <Building className="h-3 w-3" />
                    {jd.company}
                  </span>
                )}
                {jd.location && (
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {jd.location}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {formatTimeAgo(jd.created_at)}
                </span>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-slate-600 transition-colors" />
          </div>
        ))}
      </div>

      {/* View All Button */}
      {recentJobDescriptions.length > 0 && (
        <div className="pt-4 border-t border-slate-200">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleNavigateToGenerator}
            className="w-full justify-center text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg"
          >
            View All Job Descriptions
            <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
        </div>
      )}
    </div>
  );
} 